import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'rsuite';
import { canDraw, cardFacesOf, drawnCount } from '../../../shared/playtest';
import CardTile from './CardTile';
import { displayOf, refusalText } from './playtestDisplay';
import { PlaytestDropZone, useDragSourceProps } from './PlaytestDnD';
import { useEnsureCardDetails, usePlaytestSessions } from './PlaytestSessionProvider';

const SPACING = { section: 12, row: 8 };
const GRID_MIN_WIDTH = 130;
const HAND_MIN_HEIGHT = 120;
/** Opacity of a control that cannot be used right now. Matches the mobile control. */
const DISABLED_OPACITY = 0.4;

const styles = {
  hand: { display: 'flex', flexDirection: 'column', gap: SPACING.section },
  row: { display: 'flex', flexWrap: 'wrap', gap: SPACING.row, alignItems: 'center' },
  grid: {
    display: 'grid',
    gridTemplateColumns: `repeat(auto-fill, minmax(${GRID_MIN_WIDTH}px, 1fr))`,
    gap: SPACING.row,
    minHeight: HAND_MIN_HEIGHT,
  },
  column: { display: 'flex', flexDirection: 'column', gap: SPACING.row },
};

/**
 * A native button that reports `aria-disabled` from `dimmed`. rsuite's Button writes its own
 * `aria-disabled` from its `disabled` prop, and `disabled` would block the press that explains a refusal.
 */
const DimmableButton = ({ dimmed, ...props }) => <button {...props} aria-disabled={dimmed} />;
DimmableButton.propTypes = { dimmed: PropTypes.bool };

const NO_SELECTION = [];

const HAND_TARGET = { kind: 'zone', zone: 'hand' };

/**
 * The hand, the keep/mulligan decision, the draw control, and the counters.
 * While deciding, the player picks the cards to put on the bottom; tapping a
 * card in play sends it to the battlefield.
 */
const PlaytestHand = ({ collectionId, session, onOpenDetail, drops }) => {
  const { dispatchAction, requestCardDetail } = usePlaytestSessions();
  const dragSourceProps = useDragSourceProps();
  const state = session.engine;
  const deciding = state.stage === 'deciding';
  const handIds = state.zones.hand;
  const [selection, setSelection] = useState(NO_SELECTION);
  const [refusal, setRefusal] = useState(null);
  // A picked card that left the hand (played, moved or sent by a mulligan) drops out of the pick.
  const picked = selection.filter((id) => handIds.includes(id));
  const cardIds = useMemo(
    () => handIds.map((instanceId) => state.instances[instanceId].cardId),
    [handIds, state.instances]
  );
  useEnsureCardDetails(collectionId, session, cardIds);

  const pickingBottom = deciding && state.pendingBottom > 0;

  // A refused action shows its reason. Only an accepted keep or mulligan clears the pick.
  const dispatch = (action) => {
    const result = dispatchAction(collectionId, action);
    setRefusal(result === 'ok' ? null : refusalText(result));
    return result;
  };

  const keep = () => {
    if (dispatch({ type: 'keep', bottom: picked }) === 'ok') setSelection(NO_SELECTION);
  };

  const mulligan = () => {
    if (dispatch({ type: 'mulligan' }) === 'ok') setSelection(NO_SELECTION);
  };

  const toggleSelected = (instanceId) => {
    if (picked.includes(instanceId)) {
      setSelection(picked.filter((id) => id !== instanceId));
      return;
    }
    if (picked.length < state.pendingBottom) setSelection([...picked, instanceId]);
  };

  const onHandTap = (instanceId) => {
    if (deciding) {
      toggleSelected(instanceId);
      return;
    }
    dispatch({ type: 'move', instanceId, to: 'battlefield' });
  };

  const renderHandCard = (instanceId) => {
    const { cardId } = state.instances[instanceId];
    const view = displayOf(session, cardId);
    const detail = session.details.get(cardId);
    const manaCost = detail && detail.status === 'ready' ? cardFacesOf(detail.card)[0].mana_cost : null;
    return (
      <div key={instanceId} style={styles.column}>
        <CardTile
          testId={`hand-card-${instanceId}`}
          name={view.name}
          typeLine={view.typeLine}
          imageSmall={view.imageSmall}
          selected={picked.includes(instanceId)}
          onClick={() => onHandTap(instanceId)}
          dragProps={deciding ? undefined : dragSourceProps(instanceId)}
        >
          {manaCost ? <span data-testid={`hand-mana-cost-${instanceId}`}>{manaCost}</span> : null}
        </CardTile>
        <Button size="xs" data-testid={`hand-details-${instanceId}`} onClick={() => onOpenDetail(instanceId)}>
          Details
        </Button>
        {detail && detail.status === 'error' ? (
          <div style={styles.row}>
            <span data-testid={`hand-card-error-${instanceId}`}>Details unavailable</span>
            <Button
              size="xs"
              data-testid={`hand-card-retry-${instanceId}`}
              onClick={() => requestCardDetail(collectionId, cardId)}
            >
              Retry
            </Button>
          </div>
        ) : null}
      </div>
    );
  };

  const renderDecision = () => {
    if (!deciding) return null;
    if (pickingBottom) {
      return (
        <div data-testid="bottom-picker" style={styles.row}>
          <span data-testid="bottom-picker-prompt">
            {`Choose ${state.pendingBottom} card(s) to put on the bottom of your library`}
          </span>
          <span data-testid="bottom-selected-count">{`Selected ${picked.length} of ${state.pendingBottom}`}</span>
        </div>
      );
    }
    return <span data-testid="keep-prompt">Keep this hand or mulligan?</span>;
  };

  const keepEnabled = deciding && picked.length === state.pendingBottom;
  const mulliganEnabled = deciding && handIds.length > 0;
  const drawEnabled = !deciding && canDraw(state);

  return (
    <div data-testid="playtest-hand" style={styles.hand}>
      <div style={styles.row}>
        <span data-testid="counter-drawn">{drawnCount(state)}</span>
        <span>drawn</span>
        <span data-testid="counter-remaining">{state.zones.library.length}</span>
        <span>remaining in library</span>
      </div>
      {renderDecision()}
      {refusal ? <span data-testid="hand-action-error">{refusal}</span> : null}
      <div style={styles.row}>
        <Button data-testid="keep-button" disabled={!keepEnabled} onClick={keep}>
          Keep
        </Button>
        <Button data-testid="mulligan-button" disabled={!mulliganEnabled} onClick={mulligan}>
          Mulligan
        </Button>
        <Button
          as={DimmableButton}
          data-testid="draw-button"
          dimmed={!drawEnabled}
          style={drawEnabled ? undefined : { opacity: DISABLED_OPACITY }}
          onClick={() => dispatch({ type: 'draw' })}
        >
          Draw
        </Button>
      </div>
      {!deciding && !canDraw(state) ? <span data-testid="no-cards-left">Nothing left to draw</span> : null}
      <PlaytestDropZone testId="hand" target={HAND_TARGET} drops={drops} style={styles.grid}>
        {handIds.map(renderHandCard)}
      </PlaytestDropZone>
    </div>
  );
};

PlaytestHand.propTypes = {
  collectionId: PropTypes.string.isRequired,
  onOpenDetail: PropTypes.func.isRequired,
  session: PropTypes.shape({
    engine: PropTypes.object.isRequired,
    details: PropTypes.instanceOf(Map).isRequired,
    cardViews: PropTypes.instanceOf(Map).isRequired,
  }).isRequired,
  drops: PropTypes.shape({
    canDrop: PropTypes.func.isRequired,
    onDrop: PropTypes.func.isRequired,
  }).isRequired,
};

export default PlaytestHand;
